/**
 * 站点元数据与结构化数据生成。页面 SEO 元数据来自 siteContent 注册表，
 * JSON-LD 只表达页面已经展示的事实。
 */
import { BRAND_NAME, SITE_ORIGIN, buildCanonicalUrl } from './siteContent.mjs'

const BRAND_ID = `${SITE_ORIGIN}/#brand`
const WEBSITE_ID = `${SITE_ORIGIN}/#website`
export { BRAND_NAME, SITE_ORIGIN, buildCanonicalUrl }

/**
 * @param {import('./siteContent.mjs').PageDefinition | null} page
 * @returns {{ '@context': string, '@graph': object[] }}
 */
export function buildJsonLd(page) {
  const graph = [
    {
      '@type': 'WebSite',
      '@id': WEBSITE_ID,
      url: `${SITE_ORIGIN}/`,
      name: `${BRAND_NAME}官网`,
      inLanguage: 'zh-CN',
      publisher: { '@id': BRAND_ID }
    },
    {
      '@type': 'Brand',
      '@id': BRAND_ID,
      name: BRAND_NAME,
      url: `${SITE_ORIGIN}/`
    }
  ]

  if (!page) return { '@context': 'https://schema.org', '@graph': graph }

  graph.push({
    '@type': 'WebPage',
    '@id': `${page.canonical}#webpage`,
    url: page.canonical,
    name: page.title,
    description: page.description,
    inLanguage: 'zh-CN',
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': BRAND_ID }
  })

  if (page.product) {
    graph.push({
      '@type': 'Product',
      '@id': `${page.canonical}#product`,
      name: page.product.title,
      description: page.product.description,
      image: page.product.image,
      brand: { '@id': BRAND_ID },
      url: page.canonical
    })
  }

  return { '@context': 'https://schema.org', '@graph': graph }
}

/**
 * 序列化为可安全嵌入 <script> 的字符串。
 * @param {object} jsonLd
 * @returns {string}
 */
export function serializeJsonLd(jsonLd) {
  return JSON.stringify(jsonLd)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
}

/**
 * 页面自身的 SEO 头部标签源码；预渲染与运行时都写这两处，保证禁 JS 与启 JS 一致。
 * @param {import('./siteContent.mjs').PageDefinition | null} page
 * @param {{ noindex?: boolean }} [options]
 * @returns {string}
 */
export function buildHeadSource(page, options = {}) {
  const head = []
  if (options.noindex) {
    head.push('<meta name="robots" content="noindex, follow">')
  }
  if (page) {
    head.push(
      `<title>${page.title}</title>`,
      `<meta name="description" content="${page.description}">`,
      `<link rel="canonical" href="${page.canonical}">`,
      `<meta property="og:type" content="website">`,
      `<meta property="og:site_name" content="${BRAND_NAME}">`,
      `<meta property="og:title" content="${page.title}">`,
      `<meta property="og:description" content="${page.description}">`,
      `<meta property="og:url" content="${page.canonical}">`,
      `<script type="application/ld+json">${serializeJsonLd(buildJsonLd(page))}</script>`
    )
  }
  return head.join('\n')
}
